// The owner's mailbox on Papers (decision 147): connect once, read every hour, Read now, disconnect.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { formatStamp } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';
import { ErrorBox } from './ui';

const BACK = new Set(['connected', 'declined', 'google', 'state', 'signin', 'exchange']);

export function GmailCard({ owner }: { owner: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const back = params.get('gmail');
  const status = useQuery({ queryKey: qk.gmail, queryFn: ({ signal }) => api.gmailStatus(signal) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.gmail });
    void qc.invalidateQueries({ queryKey: ['papers'] });
  };
  const connect = useMutation({ mutationFn: () => api.gmailConnect(), onSuccess: ({ url }) => window.location.assign(url) });
  const disconnect = useMutation({ mutationFn: () => api.gmailDisconnect(), onSuccess: refresh });
  const read = useMutation({ mutationFn: () => api.gmailRead(), onSuccess: refresh });
  const [domains, setDomains] = useState('');
  const saved = status.data?.connection?.trustedDomains.join(', ') ?? '';
  useEffect(() => setDomains(saved), [saved]);
  const trust = useMutation({ mutationFn: () => api.gmailTrusted(domains.split(/[\s,;]+/).map((d) => d.trim().toLowerCase()).filter(Boolean)), onSuccess: refresh });
  const s = status.data;
  if (!s) return status.error ? <ErrorBox error={status.error} /> : null;
  const c = s.connection;
  return (
    <section className="card stack" aria-label={t('gmailTitle')} data-testid="gmail-card">
      <h2>{t('gmailTitle')}</h2>
      {back && BACK.has(back) ? (
        <p className={back === 'connected' ? 'alert' : 'alert alert-warn'} role="status">
          {t(`gmailBack_${back}` as AppMessageKey)}
        </p>
      ) : null}
      {!s.configured ? (
        <p className="muted">{t('gmailNotSetUp')}</p>
      ) : c ? (
        <>
          <p>{t('gmailConnected').replace('{email}', c.email).replace('{label}', c.label)}</p>
          <p className="muted">{c.lastReadAt ? t('gmailLastRead').replace('{when}', formatStamp(c.lastReadAt, intlLocale, 'day')).replace('{what}', c.lastResult ?? '') : t('gmailNeverRead')}</p>
          <div className="row-actions">
            <button type="button" className="btn btn-ghost btn-sm" disabled={read.isPending} onClick={() => read.mutate()}>
              {t('gmailReadNow')}
            </button>
            {owner ? (
              <button type="button" className="btn btn-ghost btn-sm" disabled={disconnect.isPending} onClick={() => { if (window.confirm(`${t('gmailDisconnect')}: ${t('confirmSure')}`)) disconnect.mutate(); }}>
                {t('gmailDisconnect')}
              </button>
            ) : null}
          </div>
          {read.data?.detail ? <p className="muted small">{read.data.detail}</p> : null}
          <label className="field">
            <span>{t('gmailTrusted')}</span>
            {owner ? (
              <span className="row-actions">
                <input value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="vendor.com" aria-label={t('gmailTrusted')} />
                <button type="button" className="btn btn-ghost btn-sm" disabled={trust.isPending || domains === saved} onClick={() => trust.mutate()}>
                  {t('gmailTrustedSave')}
                </button>
              </span>
            ) : (
              <strong>{saved || t('gmailTrustedNone')}</strong>
            )}
          </label>
          <p className="muted small">{c.trustedDomains.length ? t('gmailTrustedHint') : `${t('gmailTrustedNone')} ${t('gmailTrustedHint')}`}</p>
        </>
      ) : (
        <>
          <p className="muted">{t('gmailNotConnected')}</p>
          {owner ? (
            <button type="button" className="btn btn-primary btn-sm" disabled={connect.isPending} onClick={() => connect.mutate()}>
              {t('gmailConnect')}
            </button>
          ) : null}
        </>
      )}
      {connect.error || disconnect.error || read.error || trust.error ? <ErrorBox error={connect.error ?? disconnect.error ?? read.error ?? trust.error} /> : null}
    </section>
  );
}
